import { User, UserCreate } from '@/types/auth';
import { API_CONFIG } from '@/config/api';

const API_BASE_URL = API_CONFIG.baseUrl;

export class AuthApi {
  private static async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeout: number = 15000
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log('🔐 Making request to:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    let response: Response;
    try {
      response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        // CRITICAL: Use cookies for authentication
        credentials: (options.credentials as RequestCredentials) || 'include',
        ...options,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('🔐 Request timeout after', timeout, 'ms');
        throw new Error(`Request timeout - server did not respond within ${timeout / 1000} seconds.`);
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('🔐 Network error:', error.message);
        throw new Error('Network error - unable to connect to server.');
      }
      
      throw error;
    }

    console.log('🔐 Response status:', response.status);

    // Handle specific status codes
    if (response.status === 429) {
      console.warn('⚠️  Rate limited by server');
      throw new Error('RATE_LIMITED');
    }

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      try {
        const errorData = await response.json();
        console.log('🔐 Error data:', errorData);
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch (parseError) {
        errorMessage = response.statusText || errorMessage;
      }
      
      console.log('🔐 Throwing error:', errorMessage);
      throw new Error(errorMessage);
    }

    const responseData = await response.json();
    console.log('🔐 Response data:', responseData);
    return responseData;
  }

  static async register(userData: UserCreate): Promise<User> {
    const payload = {
      email: (userData as any).email,
      username: (userData as any).username,
      password: (userData as any).password,
    };

    return this.request<User>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async login(identifier: string, password: string): Promise<void> {
    const credentials = {
      email: identifier, // Backend accepts username or email in 'email' field
      password
    };

    console.log('🔐 Frontend login attempt:', { identifier, password: '***' });

    // Login sets HttpOnly cookies, no tokens returned
    return this.request<void>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  static async getCurrentUser(): Promise<User> {
    // FIXED: Only call ONE endpoint, no fallback
    // Your backend has /api/users/me working, so use that
    return this.request<User>('/api/users/me', { method: 'GET' });
  }

  static async logout(): Promise<void> {
    return this.request<void>('/api/auth/logout', {
      method: 'POST',
    });
  }

  static async deleteAccount(params: { password?: string; confirmUsername?: string }): Promise<void> {
    return this.request<void>('/api/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({
        password: params.password,
        confirm_username: params.confirmUsername,
      }),
    });
  }

  static async getGoogleAuthorizeUrl(redirectUrl: string): Promise<string> {
    const data = await this.request<{ authorization_url: string; state: string }>(
      `/api/auth/oauth/google/authorize?redirect_url=${encodeURIComponent(redirectUrl)}`,
      { method: 'GET' }
    );
    return data.authorization_url;
  }

  static async sendPhoneCode(phoneNumber: string): Promise<void> {
    return this.request<void>('/api/auth/phone/send-code', {
      method: 'POST',
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
  }

  static async verifyPhoneCode(phoneNumber: string, code: string, username?: string): Promise<User> {
    const data = await this.request<{ success: boolean; user: User }>('/api/auth/phone/verify', {
      method: 'POST',
      body: JSON.stringify({ phone_number: phoneNumber, code, username }),
    });
    return data.user;
  }
}